import { num, type ComponentForm } from '../component-types.js';
import { POST_PROFILE_FIELDS } from './post-process.js';

/**
 * `camera` (Unity's Camera). Every enabled camera renders each frame in
 * ascending depth, into its viewport rect of the screen or into a render
 * texture (`targetTexture`, sampled by materials as `rt:<name>`). There is
 * no built-in follow: move cameras from scripts (see
 * engine/docs/systems/cameras-and-post.md).
 */
const form: ComponentForm = {
  key: 'camera',
  label: 'Camera',
  icon: 'camera',
  order: 30,
  defaults: { fov: 70, near: 0.1, far: 1000, primary: true },
  fields: [
    { key: 'enabled', label: 'Enabled', type: { kind: 'bool' }, read: (v) => v.enabled ?? true },
    {
      key: 'primary',
      label: 'Primary',
      type: { kind: 'bool' },
      hint: 'The main camera: main_camera(), screen_to_ray, audio and terrain streaming use it.',
    },
    {
      key: 'clearFlags',
      label: 'Clear flags',
      type: { kind: 'enum', options: ['skybox', 'color', 'depthOnly', 'nothing'] },
      hint: 'skybox: the scene sky. color: the background color. depthOnly / nothing: draw over the cameras rendered before (overlays).',
      read: (v) => v.clearFlags ?? 'skybox',
    },
    {
      key: 'backgroundColor',
      label: 'Background',
      type: { kind: 'color', alpha: true },
      hint: 'With a target texture, alpha below 1 renders a see-through background (UI portraits).',
      showIf: (v) => v.clearFlags === 'color',
      read: (v) => v.backgroundColor ?? [0.19, 0.3, 0.47],
    },
    { key: 'projection', label: 'Projection', type: { kind: 'enum', options: ['perspective', 'orthographic'] } },
    { key: 'size', label: 'Ortho height', type: num(0.5, { min: 0.1 }), showIf: (v) => v.projection !== 'perspective' },
    { key: 'fov', label: 'Field of view', type: num(1, { min: 1, max: 179 }), showIf: (v) => v.projection !== 'orthographic' },
    { key: 'near', label: 'Near', type: num(0.01, { min: 0 }) },
    { key: 'far', label: 'Far', type: num(10, { min: 0 }) },
    {
      key: 'viewportRect',
      label: 'Viewport rect',
      type: { kind: 'json' },
      hint: '[x, y, width, height] screen fractions, origin bottom-left. [0, 0, 1, 1] = full screen; [0.5, 0, 0.5, 1] = right half.',
      read: (v) => v.viewportRect ?? [0, 0, 1, 1],
    },
    { key: 'depth', label: 'Depth', type: num(1), hint: 'Render order: lower depth renders first, higher draws on top.' },
    {
      key: 'cullingMask',
      label: 'Culling mask',
      type: { kind: 'json' },
      hint: 'Layer names this camera draws, e.g. ["Default", "Minimap"]. Empty = every layer.',
    },
    {
      key: 'targetTexture',
      label: 'Target texture',
      type: { kind: 'string', placeholder: 'screen' },
      hint: 'Render texture name (settings.render.renderTextures). Materials, sprites and UI images show it as "rt:<name>".',
    },
    {
      key: 'renderOnly',
      label: 'Render only',
      type: { kind: 'json' },
      hint: 'Entity ids, e.g. ["portrait_stage"]: this camera draws only them and their children, and no other camera draws them (UI portraits through a target texture).',
    },
    {
      key: 'targetSize',
      label: 'Target size',
      type: { kind: 'json' },
      hint: '[width, height] of the target texture when it is not declared in the project settings.',
      showIf: (v) => typeof v.targetTexture === 'string' && v.targetTexture !== '',
    },
    {
      key: 'postProcessing',
      label: 'Post-processing',
      type: { kind: 'enum', options: ['volumes', 'none', 'override'] },
      hint: 'volumes: project defaults + postProcess volumes. none: no effects. override: volumes, then the profile below.',
      read: (v) => v.postProcessing ?? 'volumes',
    },
    {
      key: 'postProfile',
      label: 'Post profile',
      type: { kind: 'object', fields: POST_PROFILE_FIELDS },
      showIf: (v) => v.postProcessing === 'override',
    },
  ],
};
export default form;
